import {
  PAYMENT_ERROR_CODES,
  COUPON_ERROR_CODES,
} from "@/lib/constants/payment-error-constants";
import { get_user_country_or_detect } from "@/lib/location/country-detection";
import {
  is_challenge_restricted,
  is_platform_restricted,
} from "@/lib/location/restricted-countries";
import { find_variant_by_id } from "@/lib/browser/checkout-utils";

/*
==================================================
 Payment Gateways UI
 - Show/hide “no payment methods” message
 - Toggle individual gateway visibility
 - Count visible gateways
==================================================
*/
export const show_no_payment_methods_message = () => {
  const no_payment_methods_message_element = document.getElementById(
    "no-payment-methods-message",
  );
  if (!no_payment_methods_message_element) return;
  no_payment_methods_message_element.classList.remove("hidden");
};

export const hide_no_payment_methods_message = () => {
  const no_payment_methods_message_element = document.getElementById(
    "no-payment-methods-message",
  );
  if (!no_payment_methods_message_element) return;
  no_payment_methods_message_element.classList.add("hidden");
};

export const set_gateway_visibility = ({ gateway_id, is_allowed }) => {
  if (!gateway_id) return;
  const gateway_selector = `.payment-gateway[data-gateway-id="${gateway_id}"]`;
  const gateway_element = document.querySelector(gateway_selector);
  if (!gateway_element) return;
  const gateway_dom_element = gateway_element;
  gateway_dom_element.style.display = is_allowed ? "" : "none";
};

export const get_visible_gateways_count = () => {
  const gateway_elements = document.querySelectorAll(".payment-gateway");
  let visible_count = 0;
  gateway_elements.forEach((gateway_node) => {
    const gateway_element = gateway_node;
    const is_hidden = (gateway_element.style.display || "") === "none";
    if (!is_hidden) visible_count++;
  });
  return visible_count;
};

/*
==================================================
 Stripe Section
 - Show/hide Stripe payment section
 - Hide all payment sections
 - Show section for selected gateway
==================================================
*/
export const show_stripe_section = () => {
  const stripe_section_element = document.getElementById(
    "stripe-payment-section",
  );
  if (!stripe_section_element) return;
  stripe_section_element.classList.remove("hidden");
};

export const hide_stripe_section = () => {
  const stripe_section_element = document.getElementById(
    "stripe-payment-section",
  );
  if (!stripe_section_element) return;
  stripe_section_element.classList.add("hidden");
};

export const hide_all_payment_sections = () => {
  const stripe_section_element = document.getElementById(
    "stripe-payment-section",
  );
  if (stripe_section_element) stripe_section_element.classList.add("hidden");
};

export const show_payment_section = ({ gateway_id }) => {
  hide_all_payment_sections();
  if (gateway_id !== "stripe") return;
  const stripe_section_element = document.getElementById(
    "stripe-payment-section",
  );
  if (!stripe_section_element) return;
  stripe_section_element.classList.remove("hidden");
};

/*
==================================================
 Checkout Error Banner
 - Show specific checkout error by code
 - Hide and reset all checkout error messages
==================================================
*/
const CHECKOUT_ERROR_BANNER_ID = "checkout-error-banner";

export const show_checkout_error = ({ error_type, order_id = undefined }) => {
  const error_banner = document.getElementById(CHECKOUT_ERROR_BANNER_ID);

  if (!error_banner) return;

  hide_all_checkout_errors();

  error_banner.classList.remove("hidden");

  const error_message_element = document.getElementById(error_type);

  if (!error_message_element) return;

  const base_message = error_message_element.dataset.baseMessage || "";
  const reference_suffix = order_id ? ` (Ref: #${order_id})` : "";

  error_message_element.textContent = base_message + reference_suffix;
  error_message_element.classList.remove("hidden");
};

export const hide_all_checkout_errors = () => {
  const error_banner = document.getElementById(CHECKOUT_ERROR_BANNER_ID);
  if (error_banner) error_banner.classList.add("hidden");
  Object.values(PAYMENT_ERROR_CODES).forEach((error_type) => {
    const message_element = document.getElementById(error_type);
    if (!message_element) return;
    message_element.classList.add("hidden");
    const base_message = message_element.dataset.baseMessage;
    if (base_message) message_element.textContent = base_message;
  });
};

/*
==================================================
 Coupon Error Banner
 - Show specific coupon error by code
 - Hide all coupon error messages
==================================================
*/
const COUPON_ERROR_BANNER_ID = "coupon-error-banner";

export const show_coupon_error = ({ error_type }) => {
  const coupon_error_banner = document.getElementById(COUPON_ERROR_BANNER_ID);
  if (!coupon_error_banner) return;
  hide_all_coupon_errors();
  coupon_error_banner.classList.remove("hidden");
  const coupon_error_message = document.getElementById(error_type);
  console.log("coupon_error_message", coupon_error_message);
  if (coupon_error_message) coupon_error_message.classList.remove("hidden");
};

export const hide_all_coupon_errors = () => {
  const coupon_error_banner = document.getElementById(COUPON_ERROR_BANNER_ID);
  if (coupon_error_banner) coupon_error_banner.classList.add("hidden");
  Object.values(COUPON_ERROR_CODES).forEach((error_type) => {
    const coupon_error_message = document.getElementById(error_type);
    if (coupon_error_message) coupon_error_message.classList.add("hidden");
  });
};

/*
==================================================
 Totals & Discounts Display
 - Update subtotal, total, discount amount, coupon code
 - Show/hide discount section
==================================================
*/
export const set_subtotal_text = ({ text }) => {
  const subtotal_element = document.getElementById("subtotal-display");
  if (subtotal_element) subtotal_element.textContent = text;
};

export const set_total_text = ({ text }) => {
  const total_element = document.getElementById("total-display");
  if (total_element) total_element.textContent = text;
};

export const show_discount_section = () => {
  const discount_section_element = document.getElementById("discount-section");
  if (!discount_section_element) return;
  discount_section_element.classList.remove("hidden");
  discount_section_element.classList.add("flex");
};

export const hide_discount_section = () => {
  const discount_section_element = document.getElementById("discount-section");
  if (!discount_section_element) return;
  discount_section_element.classList.add("hidden");
  discount_section_element.classList.remove("flex");
};

export const set_discount_amount_text = ({ text }) => {
  const discount_amount_element = document.getElementById("discount-amount");
  if (discount_amount_element) discount_amount_element.textContent = text;
};

export const set_coupon_code_display = ({ code }) => {
  const coupon_code_element = document.getElementById("coupon-code-display");
  if (coupon_code_element)
    coupon_code_element.textContent = code ? ` (${code}) ` : "";
};

/*
==================================================
 Payment Method Section
 - Show/hide section
 - Simple place order button visibility
==================================================
*/
export const show_payment_method_section = () => {
  const payment_method_section_element = document.getElementById(
    "payment-method-section",
  );
  if (payment_method_section_element)
    payment_method_section_element.classList.remove("hidden");
};

export const hide_payment_method_section = () => {
  const payment_method_section_element = document.getElementById(
    "payment-method-section",
  );
  if (payment_method_section_element)
    payment_method_section_element.classList.add("hidden");
};

export const show_place_order_button = () => {
  const place_order_button = document.getElementById("place-order-btn");
  if (place_order_button) place_order_button.classList.remove("hidden");
};

/*
==================================================
 Select Helpers
 - Populate select with values keeping placeholder
==================================================
*/
export const populate_select_options = ({ select_element, values }) => {
  if (!select_element) return;
  const placeholder_option = select_element.querySelector(
    '[data-placeholder="true"]',
  );
  select_element.innerHTML = "";
  if (placeholder_option) {
    placeholder_option.disabled = true;
    placeholder_option.selected = true;
    placeholder_option.hidden = true;
    select_element.appendChild(placeholder_option);
  }
  values.forEach((value) => {
    const option_element = document.createElement("option");
    option_element.value = value;
    option_element.textContent = value;
    select_element.appendChild(option_element);
  });
};

/*
==================================================
 Place Order Button State
 - Show processing state
 - Reset to default state
==================================================
*/
export const show_place_order_processing_in_button = () => {
  const place_order_button = document.getElementById("place-order-btn");
  if (!place_order_button) return;
  const processing_text =
    place_order_button.dataset.processingText || "Processing...";
  place_order_button.disabled = true;
  place_order_button.textContent = processing_text;
};

export const reset_place_order_button = () => {
  const place_order_button = document.getElementById("place-order-btn");
  if (!place_order_button) return;
  const default_text =
    place_order_button.dataset.defaultText ||
    place_order_button.textContent ||
    "Place Order";
  place_order_button.disabled = false;
  place_order_button.textContent = default_text;
};

/*
==================================================
 Region Modal
 - Show/hide modal
 - Hide place order when restricted
==================================================
*/
const REGION_MODAL_ID = "region-modal";
const PLACE_ORDER_BUTTON_ID = "place-order-btn";

export const show_region_modal = () => {
  const modal = document.getElementById(REGION_MODAL_ID);
  if (!modal) return;
  modal.classList.toggle("hidden", false);
  modal.classList.toggle("flex", true);
  modal.dispatchEvent(new CustomEvent("modal-shown"));
};

export const hide_region_modal = () => {
  const modal = document.getElementById(REGION_MODAL_ID);
  if (!modal) return;
  modal.classList.toggle("hidden", true);
  modal.classList.toggle("flex", false);
  modal.dispatchEvent(new CustomEvent("modal-hidden"));
};

export const hide_place_order_button = () => {
  const place_order_button = document.getElementById(PLACE_ORDER_BUTTON_ID);
  if (place_order_button) place_order_button.classList.add("hidden");
};

/*
==================================================
 Region Restrictions
 - Evaluate variant restrictions by country/platform
 - Check URL param and wiring
==================================================
*/
export const is_variant_restricted_in_country = ({ user_country, variant }) => {
  let is_restricted = false;
  if (variant.account_size && variant.plan) {
    const restricted = is_challenge_restricted(
      user_country,
      variant.account_size,
      variant.plan,
    );
    if (restricted) is_restricted = true;
  }
  if (variant.platform && !is_restricted) {
    const restricted = is_platform_restricted(user_country, variant.platform);
    if (restricted) is_restricted = true;
  }
  return is_restricted;
};

export const check_variant_region_restrictions = async ({ variant_id }) => {
  const variant = await find_variant_by_id({ variant_id });
  if (!variant) return false;

  const user_country = await get_user_country_or_detect();
  if (!user_country) return false;

  const restricted = is_variant_restricted_in_country({
    user_country,
    variant,
  });

  console.log("Variant restricted in country:", user_country);

  if (restricted) {
    show_region_modal();
    hide_place_order_button();
    return true;
  }
  hide_region_modal();
  show_place_order_button();
  return false;
};

export const check_url_region_restrictions = async () => {
  const url_params = new URLSearchParams(window.location.search);
  const variation_id = url_params.get("variation_id");
  if (!variation_id) return false;
  return await check_variant_region_restrictions({
    variant_id: Number(variation_id),
  });
};

/**
 * Listens for billing country changes to enforce region restrictions and persist session.
 * If the selected product is not available in the new country, this handler
 * will trigger the blocking modal and hide the order button.
 * It also saves the selected country to session storage.
 */
export const setup_billing_country_restriction_handler = () => {
  try {
    const billing_country_select = document.getElementById("billing_country");

    if (billing_country_select) {
      billing_country_select.addEventListener("change", async (event) => {
        // Persist user country to session storage
        const target = event.target;
        if (target && target.value) {
          sessionStorage.setItem(
            "user_country_session",
            target.value.toUpperCase(),
          );
        }

        // Check for region restrictions
        await check_url_region_restrictions();
      });
    }
  } catch (error) {
    console.log("Error in setup_billing_country_restriction_handler:", error);
  }
};

/*
==================================================
 Loading Overlay Messages
 - Rotate loading messages based on config
==================================================
*/
let message_interval = null;

const read_config = () => {
  const overlay = document.getElementById("loading-overlay");
  if (!overlay) return { messages: [], interval_ms: 2500 };
  const messages_json = overlay.getAttribute("data-messages") || "[]";
  const interval_ms = Number(overlay.getAttribute("data-interval-ms")) || 2500;
  let messages = [];
  try {
    messages = JSON.parse(messages_json) || [];
  } catch {
    messages = [];
  }
  return { messages, interval_ms };
};

const start_message_rotation = () => {
  const { messages, interval_ms } = read_config();
  const loading_title_element = document.getElementById("loading-title");
  if (!loading_title_element) return;
  if (!messages || messages.length <= 1) {
    loading_title_element.textContent = messages?.[0] || "Processing...";
    return;
  }
  let index = 0;
  loading_title_element.textContent = messages[index++];
  if (message_interval) clearInterval(message_interval);
  message_interval = window.setInterval(() => {
    const current_title_element = document.getElementById("loading-title");
    if (!current_title_element) return;
    current_title_element.textContent = messages[index];
    index = (index + 1) % messages.length;
  }, interval_ms);
};

const stop_message_rotation = () => {
  if (message_interval) {
    clearInterval(message_interval);
    message_interval = null;
  }
};

/*
==================================================
 Loading Overlay Controls
 - Show/hide overlay and manage scroll lock
==================================================
*/
export const show_loading_overlay = () => {
  const overlay = document.getElementById("loading-overlay");
  if (!overlay) return;
  overlay.classList.remove("hidden");
  overlay.classList.add("flex");
  document.body.style.overflow = "hidden";
  start_message_rotation();
};

export const hide_loading_overlay = () => {
  const overlay = document.getElementById("loading-overlay");
  if (!overlay) return;
  overlay.classList.add("hidden");
  overlay.classList.remove("flex");
  document.body.style.overflow = "";
  stop_message_rotation();
};
